import { useEffect, useRef, useState } from 'react';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useExportEndpointTests } from '@/hooks/useEndpointTests';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import type { EndpointTestExportFormat } from '@/types/endpointTests';

interface ExportTestsMenuProps {
  endpointId: string;
  /** Base file name, without the extension. */
  filename: string;
  disabled?: boolean;
}

/**
 * Export split button (task 1175): the main half exports CSV, the chevron
 * offers "Export CSV" / "Export JSON". Both read the list route's `format`
 * parameter, in the same column shape import accepts.
 */
export function ExportTestsMenu(props: ExportTestsMenuProps) {
  // Export answers to the test runs lever (task 1215).
  const runsEnabled = useFlag('innovate.fe.testing.runs');
  return runsEnabled ? <ExportTestsMenuContent {...props} /> : null;
}

function ExportTestsMenuContent({ endpointId, filename, disabled }: ExportTestsMenuProps) {
  const [open, setOpen] = useState(false);
  const exportTests = useExportEndpointTests();
  const toast = useToast();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const run = async (format: EndpointTestExportFormat) => {
    setOpen(false);
    try {
      await exportTests.mutateAsync({ endpointId, format, filename });
    } catch (err) {
      toast.error('Export failed', err instanceof Error ? err.message : undefined);
    }
  };

  const half =
    'inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50';

  return (
    <div className="relative inline-flex" ref={containerRef}>
      <button
        type="button"
        onClick={() => run('csv')}
        disabled={disabled || exportTests.isPending}
        className={`${half} rounded-l-md`}
      >
        <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
        Export
      </button>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={disabled || exportTests.isPending}
        aria-label="Export options"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${half} -ml-px rounded-r-md px-1.5`}
      >
        <ChevronDownIcon className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-20 w-36 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1"
        >
          <button type="button" role="menuitem" onClick={() => run('csv')} className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
            Export CSV
          </button>
          <button type="button" role="menuitem" onClick={() => run('json')} className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
            Export JSON
          </button>
        </div>
      )}
    </div>
  );
}

export default ExportTestsMenu;
